import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { checkPermission } from '../../../../common/utils/helpers'
import AddComment from './ResultTable/AddComment'
import RuleComment from './ResultTable/CommentModal/RuleComment'
import { ResultRuleset } from './ResultRuleset'
import { ResultTable } from './ResultTable'
import { selectRepurchaseWarrantySubmissionByVersion, selectRepurchaseWarrantyReviewResultsByVersion, selectRulesets } from '@store/reducers/submissionReviews/submissionReviewsSelectors'
import { fetchRepurchaseWarrantyReviewResults, postRuleComment } from '@store/reducers/submissionReviews/submissionReviewsSlice'
import { selectCurrentUser, selectIsAmcLender } from '@store/reducers/user/userSelectors'
import { selectSubmissionFilesByVersionAndType } from '@store/reducers/submissionFiles/submissionFilesSelectors'
import ReviewNotes from './ReviewNotes'
import {
  Button,
  Input,
  Modal,
  Switch,
  Tag,
  Tooltip,
} from 'antd'
import { Alert } from '@common/alerts'
import { ExportOutlined, FileDoneOutlined } from '@ant-design/icons'
import { RuleBadge } from './RuleBadge'
import { repurchaseWarrantyStatusToTag, WarrantyStatusIcon } from '../../Warranty/OrderWarrantyStatus'
import ReviewDatapoints from './ReviewDatapoints'
import { REPURCHASE_WARRANTY_STATUSES } from '@app/common/constants/warranty'
import { REVIEW_CATEGORIES } from '@app/common/constants/reviewCategories'
import ResultTableActions from './ResultTable/ResultTableActions'
import { ReviewLog } from './ReviewLog'
import { useFlags } from 'launchdarkly-react-client-sdk'
import ResultView from './ResultView'
import apiClient from '../../../../apiClient'
import { useDebounce } from '@app/common/hooks'
import { useReviewerActions } from '@app/pages/reviewer/hooks/useReviewerActions'


const DisplayReggoraFindings = (fieldData) => {
  const { desc, assertions, engine_outcome, data_points } = fieldData
  return (
    <>
      <div className="findings-desc">
        {desc}
      </div>
      <ReviewDatapoints {...{ assertions, engine_outcome, data_points }} />
    </>
  )
}

const baseColumns = [
  {
    title: <span className="centered">Status</span>,
    dataIndex: 'fieldData',
    width: 150,
    minWidth: 100,
    render: ({ display_outcome, outcome }) =>
      RuleBadge({ display_outcome, outcome }),
  },
  {
    title: <span>Field(s)</span>,
    dataIndex: 'field',
    minWidth: 150,
    width: 300,
  },
  {
    title: <span>Reggora Findings</span>,
    dataIndex: 'fieldData',
    minWidth: 150,
    render: (fieldData) => DisplayReggoraFindings(fieldData),
  },
]

const extendedColumns = [
  {
    title: <span>Comments</span>,
    dataIndex: 'fieldData',
    width: 150,
    minWidth: 100,
    align: 'center',
    render: (fieldData) => CommentButton(fieldData),
  },
  {
    title: <span>Actions</span>,
    dataIndex: 'fieldData',
    width: 175,
    minWidth: 200,
    align: 'center',
    render: (fieldData) => (
      <ResultTableActions fieldData={fieldData}/>
    ),
  },
  {
    title: <span>Log</span>,
    dataIndex: 'fieldData',
    width: 85,
    minWidth: 100,
    align: 'center',
    render: (fieldData) => ReviewLog({ fieldData }),
  },
]

/**
 * @param {string} order_id
 * @param {string} submissionVersion
 */
const useFetchPdfFile = (order_id, submissionVersion) => {
  const [appraisalFileUrl, setAppraisalFileUrl] = useState('')
  const [pdfParsingResult, setPdfParsingResult] = useState(null)

  const submissionFilesApi = useMemo(() => apiClient(SERVICE_URL), [])
  const parsedSubmissionFilesApi = useMemo(() => apiClient(SERVICE_URL), [])

  const appraisalPdf = useSelector((state) => selectSubmissionFilesByVersionAndType(
    state, submissionVersion, 'appraisal_pdf'), shallowEqual,
  )

  useEffect(() => {
    if (appraisalPdf && appraisalPdf.length > 0 && submissionFilesApi) {
      // Always use the standard PDF URL approach
      submissionFilesApi.get(`submission-files/v1/${appraisalPdf[0]._id}`, { presign: true })
        .then(data => {
          if (data.presigned_url) {
            setAppraisalFileUrl(data.presigned_url)
          }
        })
        .catch(error => {
          console.log(error)
        })

      // Get PDF parsing results (used for the PDF highlighting feature controlled by reggoraReviewPdfAnnotation flag)
      parsedSubmissionFilesApi.get('parsed-submission-files/results/v2', {
        order_id: order_id,
        submission_version: submissionVersion,
        file_type: 'appraisal_pdf',
        solution: 'appraisal_basic',
      }).then(parsedResults => {
        if (parsedResults?.results?.[0]?._id) {
          // Get full parsing result
          return parsedSubmissionFilesApi.get(
            `parsed-submission-files/results/v2/${parsedResults.results[0]._id}`
          )
        }
      }).then(fullPdfParsingResult => setPdfParsingResult(fullPdfParsingResult))
    } else {
      setAppraisalFileUrl('')
    }
  }, [
    appraisalPdf,
    order_id,
    parsedSubmissionFilesApi,
    submissionFilesApi,
    submissionVersion,
  ])

  return { appraisalFileUrl, pdfParsingResult }
}

/**
 * @param {string|undefined} submissionReviewId
 */
const useFetchReviewResults = (submissionReviewId, isExpandedWarranty) => {
  const dispatch = useDispatch()
  const [resultsLoading, setResultsLoading] = useState(false)

  const getReviewResults = useCallback(() => {
    if (submissionReviewId) {
      setResultsLoading(true)
      dispatch(fetchRepurchaseWarrantyReviewResults({
        result_id: submissionReviewId,
        isExpandedWarranty,
      }))
    }
  }, [dispatch, submissionReviewId, isExpandedWarranty])

  useEffect(() => {
    getReviewResults()
  }, [getReviewResults])

  return { resultsLoading, setResultsLoading }
}

const useFilterRules = (resultData) => {
  const searchInputText = useRef('')
  const [searchUpdated, setSearchUpdated] = useState(null)
  const updateSearched = useDebounce(() => {
    setSearchUpdated(Date.now())
  }, 500)

  const searchChange = (e) => {
    searchInputText.current = e.target.value
    updateSearched()
  }
  const filteredData = useMemo(() => {
    if (!resultData?.length) { return [] }

    if (!searchInputText.current) { return resultData }

    const lowerTarget = searchInputText.current.toLowerCase()

    const filteredRules = resultData.filter((item) => {
      const includesDescription = item.description.toLowerCase().includes(lowerTarget)

      if (includesDescription) { return true }

      const lowerDataPoints = item.fieldData?.data_points?.some(dp => `${dp.name} ${dp.value}`.toLowerCase().includes(lowerTarget))

      return lowerDataPoints
    })

    return filteredRules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUpdated, resultData])

  return { filteredData, searchChange }
}

const CommentButton = (fieldData) => {
  const { fieldName, id, notes, submissionReviewId, isEditable, currentUser } = fieldData
  const [comment, setComment] = useState('')
  const dispatch = useDispatch()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const showModal = () => { setIsModalOpen(true) }
  const handleOk = () => {
    dispatch(postRuleComment({
      note: comment,
      ruleId: id,
      submissionReviewId: submissionReviewId,
      user_id: currentUser.id,
      user_type: 'LENDER',
    }))
    setComment('')
    setIsModalOpen(false)
  }
  const handleCancel = () => { setIsModalOpen(false) }
  const handleCommentInputChange = (e) => {
    setComment(e.target.value)
  }
  return (
    <>
      <div className="comment-label">
        {(isEditable) ? (
          <Button onClick={showModal}>
            {notes?.length > 0 ? `View(${notes.length})` : '+'}
          </Button>
        ) : (
          <Button onClick={showModal}>
            {notes?.length > 0 ? `View(${notes.length})` : null}
          </Button>
        )}
      </div>
      <Modal
        className="comment-modal"
        closable={false}
        onCancel={handleCancel}
        onOk={handleOk}
        style={{ maxWidth: '415px' }}
        visible={isModalOpen}
        footer={(isEditable && checkPermission('reggora_review_action')) ? [
          <Button key="back" onClick={handleCancel}>
              Cancel
          </Button>,
          <Button
            key="submit"
            onClick={handleOk}
            type="primary"
          >
              Add
          </Button>,
        ] : <div></div>}
      >
        <RuleComment
          commentData={notes}
          currentUser={currentUser}
          fieldName={fieldName}
        />
        {isEditable &&
            <AddComment
              comment={comment}
              maxRows={5}
              minRows={1}
              onChange={handleCommentInputChange}
            />
        }
      </Modal>
    </>
  )
}

const WarrantyStatusBanner = ({ repurchaseWarranty }) => {
  const { messages: rawMessages, status } = repurchaseWarranty || {}
  const messages = rawMessages?.filter(Boolean)
  const color = repurchaseWarrantyStatusToTag[status]
  const messageByStatus = {
    [REPURCHASE_WARRANTY_STATUSES.WARRANTY_GRANTED]: 'This appraisal is qualified for Reggora Warranty',
    [REPURCHASE_WARRANTY_STATUSES.WARRANTY_DECLINED]: 'This appraisal does not qualify for Reggora Warranty',
  }
  const message = messageByStatus[status]

  if (!message) { return null }

  return (
    <div className='repurchase-warranty__banner'>
      <Tag
        className='h5 status-bar d-flex flex-column'
        color={color}
        style={{ padding: '0.5rem 1rem', margin: '0', width: '100%' }}
      >
        <div
          className='status_message'
          data-test-id="warranty-banner"
          style={{ marginBottom: messages?.length > 0 ? '0.5rem' : '0' }}
        >
          <WarrantyStatusIcon status={status} />
          <span className='ml-1'>{message}</span>
        </div>
        {messages?.map((msg, index) => (
          <div data-test-id={`warranty-banner-message-${index + 1}`} key={index}>{msg}</div>
        ))}
      </Tag>
    </div>
  )
}

export function RepurchaseWarranty(props) {
  const {
    isEditable,
    orderData,
    submission,
    submissionVersion,
  } = props

  const { navigateToReviewerFromOrderId } = useReviewerActions()
  const isAmcLender = useSelector(selectIsAmcLender)
  const flags = useFlags()
  const isExpandedWarranty = (flags.ampManualExpandedWarranty && isAmcLender) || false
  const columns = React.useMemo(
    () => (isExpandedWarranty ? [...baseColumns, ...extendedColumns] : baseColumns),
    [isExpandedWarranty]
  )

  const [resultData, setResultData] = useState([])
  const [showTableView, setShowTableView] = useState(true)
  const rulesets = useSelector((state) => selectRulesets(state))
  const submissionReview = useSelector((state) => selectRepurchaseWarrantySubmissionByVersion(state, submissionVersion))
  const reviewResults = useSelector((state) => selectRepurchaseWarrantyReviewResultsByVersion(state, submissionVersion))
  const [resultRulesetName, setResultRulesetName] = useState({
    name: '',
    key: '',
  })
  const currentUser = useSelector(selectCurrentUser)

  const { resultsLoading, setResultsLoading } = useFetchReviewResults(submissionReview?._id, isExpandedWarranty)
  const { appraisalFileUrl, pdfParsingResult } = useFetchPdfFile(orderData.id, submissionVersion)
  const { filteredData, searchChange } = useFilterRules(resultData)

  useEffect(() => {
    if (rulesets && submissionReview) {
      if (rulesets.length === 0 || submissionReview.review_result?.length === 0) {
        setResultRulesetName({
          name: '',
          key: '',
        })
        return
      }

      const ruleset = rulesets.find(ruleset => ruleset.key === submissionReview.review_result?.[0].rule_set_key)
      if (ruleset) {
        setResultRulesetName({
          name: ruleset.name,
          key: ruleset.key,
        })
        return
      }

      setResultRulesetName({
        name: '',
        key: '',
      })
    }
  }, [rulesets, submissionReview])


  useEffect(() => {
    const parsedRules = reviewResults ? Object.values(reviewResults).map((rule) => {
      return {
        key: rule._id,
        status: {
          ruleName: rule.rule_name,
          engine_outcome: rule.engine_outcome,
        },
        description: rule.lender_message,
        field: rule.fields_message,
        vendor_description: rule.vendor_message,
        fieldData: {
          assertions: rule.assertions,
          currentUser: currentUser,
          data_points_by_submission: rule?.data_points_by_submission || {},
          data_points: rule?.data_points || [],
          desc: rule.lender_message,
          display_outcome: rule.display_outcome,
          engine_outcome: rule.engine_outcome,
          fieldName: rule.fields_message,
          hasSystemOverride: rule.hasSystemOverride,
          id: rule._id,
          isEditable: isEditable,
          notes: rule.note_logs,
          outcome_override_logs: rule.outcome_override_logs,
          outcome: rule.outcome,
          submissionReviewId: rule.submission_review_result_id,
          submissionVersion: submission.version,
        },
        raw_rule: rule,
      }
    }) : []
    setResultData(parsedRules)
  }, [reviewResults, submission.version, isEditable, currentUser])

  useEffect(() => {
    if (reviewResults && resultsLoading) {
      setResultsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewResults, resultsLoading])

  const switchView = (isChecked) => {
    setShowTableView(!isChecked)
  }

  const onNavigateToNewReviewer = () => {
    navigateToReviewerFromOrderId(orderData.id, submission.version, false, REVIEW_CATEGORIES.WARRANTY)
  }

  const onNavigateToNewReviewerInNewTab = (e) => {
    navigateToReviewerFromOrderId(orderData.id, submission.version, true, REVIEW_CATEGORIES.WARRANTY)
  }

  const handleFullViewMouseDown = (e) => {
    // Middle click (scroll wheel click) - button === 1
    if (e.button === 1) {
      e.preventDefault()
      navigateToReviewerFromOrderId(orderData.id, submission.version, true, REVIEW_CATEGORIES.WARRANTY)
    }
  }

  if (!submissionReview) {
    return (
      <div className='submission-tab-content repurchase-warranty__container'>
        <div className='submission-tab-content__header'>
          <div className='mr-3'>
            <Alert data-test-id="warranty-banner" title={<><div>This submission has not yet undergone Reggora Warranty.</div><div>This submission must be submitted to UCDP before Reggora Warranty can be determined.</div></>} type="waiting" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='submission-tab-content repurchase-warranty__container'>
      <div className='submission-tab-content__header'>
        <div className='mr-3'>
          <ResultRuleset resultRuleset={resultRulesetName} createdDate={submissionReview.created} />
        </div>
        <div className='flex-grow-1 ml-3'>
          <WarrantyStatusBanner repurchaseWarranty={submission?.repurchase_warranty} />
        </div>
        {
          isExpandedWarranty && (
            <>
              <div className='d-flex ml-3 mr-0 align-items-center'>
                <span className={'mx-2'}>Search</span>
                <Input allowClear onChange={searchChange}></Input>
              </div>
              <div className='d-flex ml-3 mr-0'>
                <span className={showTableView ? 'mx-1 font-weight-bold' : 'mx-1'}>Table</span>
                <Switch className='review-toggle-switch' onChange={(value) => switchView(value)}></Switch>
                <span className={!showTableView ? 'mx-1 font-weight-bold' : 'mx-1'}>PDF</span>
              </div>
            </>
          )
        }

        {flags?.enableNewReviewer && <>
          <Button
            onClick={onNavigateToNewReviewer}
            onMouseDown={handleFullViewMouseDown}
            type="primary"
            ghost
            icon={<FileDoneOutlined />}
            className='reggora-review-button'
            data-test-id="reviewer-full-view-button"
          >
            Full View
          </Button>
          <Tooltip placement="bottom" title="Open in new tab"><Button type="primary" className='reggora-review-button-new-tab' ghost icon={<ExportOutlined />} onClick={onNavigateToNewReviewerInNewTab}></Button></Tooltip>
        </>}
      </div>
      <div className='submission-tab-content__body d-block'>
        {submission?.repurchase_warranty?.status !== 'warranty_granted' && (
          <>
            {showTableView ? (
              <ResultTable columns={columns} data={filteredData} isLoading={resultsLoading} />
            ) : (
              <ResultView
                data={filteredData}
                fileUrl={appraisalFileUrl}
                pdfParsingResult={pdfParsingResult}
                RuleBadge={RuleBadge}
              />
            )}
            <ReviewNotes
              isEditable={isEditable}
              orderId={orderData.id}
              submissionVersion={submissionVersion}
            />
          </>
        )}
      </div>
    </div>
  )
}
